import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import withSizes from 'react-sizes';
import Grid from '@material-ui/core/Grid';
import SectionTitle from 'containers/TopicCenter/components/SectionTitle';
import Carousel from 'components/ResourcesCarousel';
import ResourceItemSelector from 'components/ResourceItemSelector';
import TrendingTopic from 'containers/HomePage/components/TrendingTopic';
import ListInfo from './ListInfo';

const useStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.down('480')]: {
      position: 'relative',
    },
    marginTop: 20,
  },
  viewMore: {
    [theme.breakpoints.down('480')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      margin: 0,
      padding: '150px 0 40px 0 !important',
      backgroundImage:
        'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.74), rgba(255,255,255,1))',
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  carouselContainer: {
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    position: 'relative',
  },
  viewAllContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const ListCarousel = ({
  data,
  grayscale,
  isMobile,
  isTablet,
  isBigTablet,
  isDesktop,
  linkLabel,
  personalizedCard,
  displayAll,
  useClientImage,
  assessmentsDurationAltPosition,
}) => {
  const classes = useStyles();
  const carouselRef = useRef();
  const style = _get(data, 'fields.style');
  const slidesToSlide = isMobile
    ? 1
    : isTablet
    ? 2
    : isBigTablet
    ? 3
    : isDesktop
    ? personalizedCard
      ? 3
      : 4
    : personalizedCard
    ? 3
    : 5;
  const resources =
    slidesToSlide === 5
      ? (data.resources || _get(data, 'fields.resources') || []).slice(0, 10)
      : (data.resources || _get(data, 'fields.resources') || []).slice(0, 8);
  if (_isEmpty(resources)) return null;

  const setRef = el => {
    carouselRef.current = el;
  };

  const withListInfo = style === 'Intro' && isDesktop;
  const carouselContent = (
    <Carousel
      isSmallCard
      setRef={setRef}
      slidesToSlide={slidesToSlide}
      swipeable
      responsive={{
        superLargeDesktop: {
          breakpoint: { max: 4000, min: 1300 },
          items: withListInfo ? 3 : personalizedCard ? 3 : 5,
        },
        desktop: {
          breakpoint: {
            max: 1300,
            min: 960,
          },
          items: withListInfo ? 2 : personalizedCard ? 3 : 4,
        },
        bigTablet: {
          breakpoint: {
            max: 960,
            min: 768,
          },
          items: 3,
        },
        tablet: {
          breakpoint: {
            max: 768,
            min: 480,
          },
          items: 2,
        },
        mobile: {
          breakpoint: {
            max: 480,
            min: 0,
          },
          items: 1,
        },
      }}
    >
      {resources.map((item, index) => {
        const fromContentful = _has(item, 'fields');
        const finalId = fromContentful ? item.sys.id : item.objectID;

        if (style === 'Topic' && !personalizedCard && !displayAll) {
          const isTopic = ['Topics', 'topics'].includes(
            fromContentful ? item.sys.contentType.sys.id : item.expandedType,
          );
          if (!isTopic) return null;
          let finalFields = item;
          if (_has(item, 'fields')) {
            finalFields = {
              ...item.fields,
              id: item.sys.id,
              type: 'Topics',
            };
          }
          return (
            <TrendingTopic
              topicID={finalFields.id}
              fields={finalFields}
              key={finalFields.slug}
              link={`/topics/${finalFields.slug}`}
              grayscale={grayscale}
            />
          );
        }

        return (
          <ResourceItemSelector
            key={finalId}
            item={item}
            itemIndex={index}
            personalizedCard={personalizedCard}
            grayscale={grayscale}
            useClientImage={useClientImage}
            assessmentsDurationAltPosition={assessmentsDurationAltPosition}
          />
        );
      })}
    </Carousel>
  );
  return (
    <Grid item xs={12}>
      <Grid container spacing={2} className={classes.root}>
        <SectionTitle
          title={_get(data, 'fields.title')}
          link={`/lists/${_get(data, 'fields.slug')}`}
          linkLabel={linkLabel}
        />
        {withListInfo ? (
          <Grid item xs={12}>
            <Grid container>
              <Grid xs={4}>
                <ListInfo data={data} />
              </Grid>
              <Grid item xs={8} className={classes.carouselContainer}>
                {carouselContent}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12} className={classes.carouselContainer}>
            {carouselContent}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

ListCarousel.defaultProps = {
  displayAll: false,
};
ListCarousel.propTypes = {
  data: PropTypes.object,
  grayscale: PropTypes.bool,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isBigTablet: PropTypes.bool,
  linkLabel: PropTypes.string,
  displayAll: PropTypes.bool,
  assessmentsDurationAltPosition: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isTablet: width <= 768,
  isBigTablet: width <= 960,
  isDesktop: width <= 1300,
});

export default withSizes(mapSizesToProps)(ListCarousel);
